import { useCallback, type FunctionComponent } from 'react';

import { ErrorBoundary } from '@trello/error-boundaries';
import { useFeatureGate } from '@trello/feature-gate-client';
import { idCache } from '@trello/id-cache';
import {
  BoardIdProvider,
  EnterpriseIdProvider,
  WorkspaceIdProvider,
} from '@trello/id-context';
import { isActiveRoute, RouteId, routerState } from '@trello/router';
import { useSharedStateSelector } from '@trello/shared-state';

import { useBoardInformationFragment } from 'app/src/components/Board/BoardInformationFragment.generated';
import { SplitScreenOnboarding } from 'app/src/components/SplitScreenOnboarding';
import { LazyPPOnboardingModal } from './LazyPPOnboardingModal';
import { PPOnboardingModalErrorHandler } from './PPOnboardingModalErrorHandler';

export const PPOnboardingModalWithProviders: FunctionComponent<{
  onComplete: () => void;
}> = ({ onComplete }) => {
  const { value: isM3Enabled } = useFeatureGate('trello_pp_discovery_m3');
  const { shortLinkOrId } = useSharedStateSelector(
    routerState,
    useCallback((state) => {
      const isRelevantRoute =
        isActiveRoute(state, RouteId.BOARD) ||
        isActiveRoute(state, RouteId.INVITE_ACCEPT_BOARD) ||
        isActiveRoute(state, RouteId.CARD);
      const shortLink = isRelevantRoute ? state.params?.shortLink : null;
      return {
        shortLinkOrId: shortLink,
      };
    }, []),
  );

  const boardId = idCache.getBoardId(shortLinkOrId ?? '') || '';

  const { data: board } = useBoardInformationFragment({
    from: { id: boardId },
    optimistic: true,
  });

  const workspaceId = board?.idOrganization ?? null;
  const enterpriseId = board?.idEnterprise ?? null;

  return (
    <EnterpriseIdProvider value={enterpriseId}>
      <WorkspaceIdProvider value={workspaceId}>
        <BoardIdProvider value={boardId}>
          {isM3Enabled ? (
            <LazyPPOnboardingModal onComplete={onComplete} />
          ) : (
            <SplitScreenOnboarding onComplete={onComplete} />
          )}
        </BoardIdProvider>
      </WorkspaceIdProvider>
    </EnterpriseIdProvider>
  );
};

export const PPOnboardingModalContainer: FunctionComponent<{
  onComplete: () => void;
}> = ({ onComplete }) => {
  return (
    <ErrorBoundary
      errorHandlerComponent={() => (
        <PPOnboardingModalErrorHandler onComplete={onComplete} />
      )}
      tags={{
        ownershipArea: 'trello-cross-flow',
        feature: 'SplitScreen Onboarding',
      }}
    >
      <PPOnboardingModalWithProviders onComplete={onComplete} />
    </ErrorBoundary>
  );
};
